import { useState, useEffect, createContext, useContext } from 'react';
import { API_ENDPOINTS } from '../config/api';
import { createAuthHeaders, clearSessionToken, getSessionToken, extractSessionFromUrl } from './useSessionToken';

const DEBUG = import.meta.env.DEV;

interface User {
  id: string;
  name: string;
  email: string;
  tier: 'STANDARD' | 'SILVER' | 'GOLD' | null;
  role: 'PARTNER' | 'ADMIN' | 'SUPER_ADMIN';
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  login: (email: string) => Promise<{ link?: string }>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check session immediately on mount
  useEffect(() => {
    checkSession();
  }, []);

  async function checkSession() {
    if (DEBUG) console.log('[Auth] Checking session...');
    
    // Always try to extract from URL first (handles auth redirects)
    extractSessionFromUrl();
    
    const sessionInStorage = getSessionToken();
    if (DEBUG) console.log('[Auth] Session in localStorage:', sessionInStorage ? 'found' : 'NOT found');
    
    try {
      const headers = createAuthHeaders();
      
      // Try partner endpoint first (Bearer token auth, no cookies needed)
      if (DEBUG) console.log('[Auth] Trying /api/partner/me...');
      let response = await fetch(API_ENDPOINTS.me, {
        headers,
      });
      if (DEBUG) console.log('[Auth] Partner response:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        setUser({
          id: data.id,
          name: data.name,
          email: data.email,
          tier: data.tier,
          role: 'PARTNER',
        });
        setLoading(false);
        return;
      }
      
      // If partner fails, try admin endpoint (Bearer token auth, no cookies needed)
      if (DEBUG) console.log('[Auth] Trying /api/admin/me...');
      response = await fetch(API_ENDPOINTS.adminMe, {
        headers,
      });
      if (DEBUG) console.log('[Auth] Admin response:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        setUser({
          id: data.id,
          name: data.name,
          email: data.email,
          tier: data.tier,
          role: data.role,
        });
      } else {
        // Both partner and admin failed - token is invalid, clear it
        if (DEBUG) console.log('[Auth] Both auth checks failed, clearing invalid token');
        clearSessionToken();
      }
    } catch (err) {
      if (DEBUG) console.error('[Auth] Session check failed:', err);
    } finally {
      setLoading(false);
    }
  }

  async function login(email: string): Promise<{ link?: string }> {
    setLoading(true);
    setError(null);

    try {
      // Unified endpoint. Worker resolves admin vs partner internally based
      // on which table the email belongs to — no client-side heuristic.
      const response = await fetch(API_ENDPOINTS.magicLink, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Eroare la trimiterea link-ului');
      }

      const data = await response.json();
      return { link: data.link };
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Eroare necunoscută');
      throw err;
    } finally {
      setLoading(false);
    }
  }

  async function logout() {
    try {
      await fetch(API_ENDPOINTS.logout, {
        method: 'POST',
        headers: createAuthHeaders(),
      });
      clearSessionToken();
      setUser(null);
    } catch (err) {
      console.error('[Auth] Logout error:', err);
    }
  }

  return (
    <AuthContext.Provider value={{ user, loading, error, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext };